import XLSX from 'xlsx/xlsx.js';
import type { ParsedWorkbook } from './partner-import-types.js';
import { normalizeSchedulingKey, normalizeSchedulingText } from '../ticketing/partner-scheduling-patterns.js';
import type { PartnerSchedulingClientRecordInput, PartnerSchedulingPartnerInput } from './partner-import-types.js';
import { A } from '@adornis/base/env-info.js';
import {
  getClientNameFromIndexSheet,
  getOrderedPartnerColumnIndexes,
  isCountryNotSupportedInNotes,
  looksLikePartnerName,
  sheetRows,
} from './partner-workbook-extract.js';
import { shouldSkipNonIndexDataRow, shouldStopIndexSheetAtRow } from './partner-import-row-guards.js';
import {
  extractPrimarySupportContact,
  normalizeSchedulingPartnerName,
  PARTNER_CONTACT_INFORMATION,
} from './partner-workbook-extract-partner.js';

/** Client index sheets are named like `Index - {ClientName}`. */
function isClientSheet(sheetName: string): boolean {
  return normalizeSchedulingKey(sheetName)
    .toLocaleLowerCase()
    .startsWith(normalizeSchedulingKey('Index').toLocaleLowerCase());
}

/** Partner detail sheets contain a "Partner Contact Information" section. */
function isPartnerSheet(rows: Array<Array<string | number | null>>): boolean {
  return rows.some(row =>
    row.some(cell =>
      String(cell ?? '')
        .toLocaleLowerCase()
        .includes(PARTNER_CONTACT_INFORMATION),
    ),
  );
}
// Find the index of the header row in the rows
function findHeaderRowIndex(rows: Array<Array<string | number | null>>) {
  return rows.findIndex(row => {
    const headers = row.map(value => normalizeSchedulingKey(String(value ?? '')));
    const hasGeo = headers.includes('region') || headers.includes('country');
    const hasPartnerCols = headers.some(h => h.includes('partner'));
    return hasGeo && hasPartnerCols;
  });
}

// Parse a partner workbook and return the parsed data.
export function parsePartnerWorkbook(buffer: Buffer, sourceFilename: string): ParsedWorkbook {
  const workbook = XLSX.read(buffer, { type: 'buffer' });

  const allSheetNames = workbook.SheetNames;
  const messages: string[] = [];
  const clients: PartnerSchedulingClientRecordInput[] = [];
  const partners: PartnerSchedulingPartnerInput[] = [];

  for (const sheetName of allSheetNames) {
    try {
      const clientName = getClientNameFromIndexSheet(sheetName);
      // Check exists client name in the workbook
      const clientExists = clients.some(c => c.clientName === clientName);
      if (clientExists) {
        messages.push(`|${sheetName}: 🚫🚫🚫 Skipped (client name already exists)`);
        continue;
      }
      const rows = sheetRows(workbook.Sheets[sheetName]);
      if (isClientSheet(sheetName)) {
        const headerRowIndex = findHeaderRowIndex(rows);
        if (headerRowIndex < 0) {
          messages.push(`|${sheetName}: ⏭️ Skipped (not a valid client sheet)`);
          continue;
        }
        const headers = (rows[headerRowIndex] ?? []).map(value => normalizeSchedulingKey(String(value ?? '')));
        const regionColumnIndex = headers.findIndex(h => h === 'region');
        const countryColumnIndex = headers.findIndex(h => h === 'country');
        const addressColumnIndex = headers.findIndex(h => h === 'address');
        const cityColumnIndex = headers.findIndex(h => h === 'city');
        const zipCodeColumnIndex = headers.findIndex(h => h === 'zip code');
        const requiredSlaIndex = headers.findIndex(h => h === 'required sla');
        const importantNotesIndex = headers.findIndex(h => h.includes('note'));
        const partnerColumnIndexes = getOrderedPartnerColumnIndexes(headers);

        if (partnerColumnIndexes.length === 0 || regionColumnIndex < 0 || countryColumnIndex < 0) {
          messages.push(`|${sheetName}: ⏭️ Skipped (not a valid client sheet)`);
          continue;
        }
        messages.push(`|${sheetName}: ℹ️ Importing`);
        let importedRows = 0;
        for (const [rowIndex, row] of rows.slice(headerRowIndex + 1).entries()) {
          const sheetRow = rowIndex + headerRowIndex + 2;
          const values = row.map(value => normalizeSchedulingText(String(value ?? ''))) as string[];
          const region = regionColumnIndex >= 0 ? (values[regionColumnIndex] ?? '') : '';
          const country = countryColumnIndex >= 0 ? (values[countryColumnIndex] ?? '') : '';
          const requiredSla = requiredSlaIndex >= 0 ? (values[requiredSlaIndex] ?? '') : '';
          const city = cityColumnIndex >= 0 ? (values[cityColumnIndex] ?? '') : '';
          const address = addressColumnIndex >= 0 ? (values[addressColumnIndex] ?? '') : '';
          const zipCode = zipCodeColumnIndex >= 0 ? (values[zipCodeColumnIndex] ?? '') : '';

          if (importantNotesIndex >= 0 && isCountryNotSupportedInNotes(values[importantNotesIndex] ?? '')) {
            messages.push(`|---- ${sheetRow}: ⏭️ Skipped (not support)`);
            continue;
          }

          if (shouldStopIndexSheetAtRow(region, country)) {
            messages.push(
              `|---- ${sheetRow}: ⏭️ Stopped (non-index section (e.g. PARTNER/site list). Rows below were not imported.)`,
            );
            break;
          }

          if (shouldSkipNonIndexDataRow(region, country, city, address)) {
            messages.push(`|---- ${sheetRow}: ⏭️ Skipped (non-index data row)`);
            continue;
          }
          if (!region && !country && !city && !address) {
            messages.push(`|---- ${sheetRow}: ⏭️ Skipped (no data row)`);
            continue;
          }

          const partnerNamesInRow = partnerColumnIndexes
            .map(colIdx => String(values[colIdx] ?? '').trim())
            .filter(looksLikePartnerName);
          clients.push({
            _id: A.getGloballyUniqueID(),
            clientName,
            clientKey: normalizeSchedulingKey(clientName),
            region,
            country,
            requiredSla,
            city,
            address,
            zipCode,
            partners: [...new Set(partnerNamesInRow.map(normalizeSchedulingPartnerName))],
          });
          importedRows += 1;
        }
        messages.push(`|${sheetName}: ✅ Imported ${importedRows} rows`);
      } else if (isPartnerSheet(rows)) {
        const { name, email, phone } = extractPrimarySupportContact(workbook.Sheets[sheetName]);
        const partnerName = normalizeSchedulingPartnerName(sheetName);
        const partnerExists = partners.some(p => p.partnerName === partnerName);
        if (partnerExists) {
          messages.push(`|${sheetName}: 🚫🚫🚫 Skipped (partner name already exists)`);
          continue;
        }

        if (!email) {
          messages.push(`|${sheetName}: 🚫🚫🚫 Skipped (no email found)`);
          continue;
        }

        const partner = {
          _id: A.getGloballyUniqueID(),
          partnerName,
          contactEmail: email,
          contactName: name ?? '',
          contactPhone: phone ?? '',
        };
        partners.push(partner);
        messages.push(
          `|${sheetName}: ✅ Imported partner: ${partnerName} (name: ${name}, email: ${email}, phone: ${phone})`,
        );
      } else {
        messages.push(`|${sheetName}: ⏭️ Skipped (not a valid client or partner sheet)`);
      }
    } catch (error) {
      messages.push(`|${sheetName}: ❌ Error: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  // Drop index partner names that have no matching partner tab (no contact row imported).
  const partnerNamesWithoutSheet = new Set<string>();
  for (const client of clients) {
    for (const partnerName of client.partners) {
      const partner = partners.find(p => p.partnerName.toLowerCase() === partnerName.toLowerCase());
      if (!partner) partnerNamesWithoutSheet.add(partnerName);
    }
  }
  if (partnerNamesWithoutSheet.size > 0) {
    for (const partnerName of Array.from(partnerNamesWithoutSheet).slice(0, 5)) {
      messages.push(`|❌ Partner listed on index but no partner sheet: ${partnerName}`);
    }
    messages.push(
      `|ℹ️ ${partnerNamesWithoutSheet.size} partner names were listed on index sheets but had no matching partner sheet (and were thus not imported).`,
    );
  }

  for (const client of clients) {
    client.partners = client.partners.filter(p => !partnerNamesWithoutSheet.has(p));
  }

  const clientsWithNoPartnersAfterLinking = clients.filter(c => c.partners.length === 0);
  if (clientsWithNoPartnersAfterLinking.length > 0) {
    for (const client of Array.from(clientsWithNoPartnersAfterLinking).slice(0, 5)) {
      messages.push(
        `|❌ Client has no partners: ${client.clientName} - Region: ${client.region} - Country: ${client.country}`,
      );
    }
    messages.push(
      `|ℹ️ ${clientsWithNoPartnersAfterLinking.length} clients had no partners after linking to partner sheets. These clients were not imported.`,
    );
  }
  const clientsWithPartners = clients.filter(c => c.partners.length > 0);

  return { clients: clientsWithPartners, partners, messages };
}
